'use client'
import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Award, ChevronRight, Trophy, Sparkles, Eye, Flame, ShieldCheck, Crown, Lock } from 'lucide-react'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { useTranslation } from '@/lib/i18n'
import { AchievementBadge } from './medallions/achievement-badge'
import { achievementText, type Translate } from './catalogue-text'

export interface AchievementItem {
  id: string
  name: string
  desc: string
  state: 'unlocked' | 'progress' | 'locked'
  color: string
  iconKey: 'trophy' | 'sparkles' | 'eye' | 'flame' | 'shield' | 'crown' | 'flag'
  progress?: number
  total?: number
  /**
   * When it was unlocked, as a complete relative phrase in the reader's
   * language ("5m ago"). The tile prints it after "Unlocked" as given; it
   * used to append its own "ago" and read "Unlocked 5m ago ago".
   */
  unlockedAgo?: string
}

export interface AchievementsRowProps {
  items: AchievementItem[]
  /** DEPRECATED: "See all" now opens an in-place drawer instead of navigating. */
  seeAllHref?: string
}

const ICONS: Record<AchievementItem['iconKey'], React.ReactNode> = {
  trophy: <Trophy className="h-5 w-5" />,
  flag: <Trophy className="h-5 w-5" />,
  sparkles: <Sparkles className="h-5 w-5" />,
  eye: <Eye className="h-5 w-5" />,
  flame: <Flame className="h-5 w-5" />,
  shield: <ShieldCheck className="h-5 w-5" />,
  crown: <Crown className="h-5 w-5" />,
}

function AchievementTile({ a, t }: { a: AchievementItem; t: Translate }) {
  const text = achievementText(t, a)
  return (
    <div
      className={
        'relative flex flex-col items-center rounded-xl border p-3 text-center transition-colors ' +
        (a.state === 'unlocked'
          ? 'border-border bg-muted/40'
          : a.state === 'progress'
            ? 'border-border bg-background'
            : 'border-dashed border-border bg-transparent')
      }
    >
      {a.state === 'locked' && <Lock className="absolute end-2 top-2 h-3 w-3 text-muted-foreground" />}
      <AchievementBadge color={a.color} icon={ICONS[a.iconKey]} size={52} state={a.state} />
      <div className="mt-2 line-clamp-1 text-xs font-semibold text-foreground">{text.name}</div>
      <div className="mt-0.5 line-clamp-2 text-[10px] text-muted-foreground">{text.desc}</div>
      {a.state === 'progress' && a.progress != null && a.total != null && (
        <div className="mt-2 w-full">
          <Progress value={(a.progress / a.total) * 100} className="h-1" />
          <div className="mt-1 text-[10px] tabular-nums text-muted-foreground">{a.progress}/{a.total}</div>
        </div>
      )}
      {a.state === 'unlocked' && (
        <div className="mt-2 text-[10px] text-emerald-600 dark:text-emerald-400">
          {a.unlockedAgo
            ? t('learner.cards.achievements.unlockedAgo', { when: a.unlockedAgo })
            : t('learner.cards.achievements.unlocked')}
        </div>
      )}
      {a.state === 'locked' && <div className="mt-2 text-[10px] text-muted-foreground">{t('learner.cards.achievements.locked')}</div>}
    </div>
  )
}

export function AchievementsRow({ items }: AchievementsRowProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const unlocked = items.filter((a) => a.state === 'unlocked')
  const inProgress = items.filter((a) => a.state === 'progress')
  const locked = items.filter((a) => a.state === 'locked')
  // Show earned first, then closest-to-earned, so the row never looks empty.
  const preview = [...unlocked, ...inProgress, ...locked].slice(0, 6)

  const groups: { key: string; label: string; hint: string; list: AchievementItem[] }[] = [
    {
      key: 'earned',
      label: t('learner.cards.achievements.groups.earned'),
      hint: t('learner.cards.achievements.groups.earnedHint'),
      list: unlocked,
    },
    {
      key: 'progress',
      label: t('learner.cards.achievements.groups.inProgress'),
      hint: t('learner.cards.achievements.groups.inProgressHint'),
      list: inProgress,
    },
    {
      key: 'locked',
      label: t('learner.cards.achievements.groups.locked'),
      hint: t('learner.cards.achievements.groups.lockedHint'),
      list: locked,
    },
  ]

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2">
          <Award className="h-5 w-5 text-amber-500" /> {t('learner.cards.achievements.title')}
          <Badge variant="outline" className="ms-1 text-[10px]">
            {t('learner.cards.achievements.summary', {
              earned: unlocked.length,
              inProgress: inProgress.length,
              locked: locked.length,
            })}
          </Badge>
        </CardTitle>
        {/* Opens an IN-PLACE drawer (was a navigation to /play/me — the learn page). */}
        <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground" onClick={() => setOpen(true)}>
          {t('learner.cards.common.seeAll')} <ChevronRight className="h-4 w-4 rtl-flip" />
        </Button>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {preview.map((a) => (
            <AchievementTile key={a.id} a={a} t={t} />
          ))}
        </div>
      </CardContent>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-amber-500" /> {t('learner.cards.achievements.title')}
            </SheetTitle>
            <SheetDescription>
              {t('learner.cards.achievements.sheetSummary', {
                earned: unlocked.length,
                total: items.length,
                inProgress: inProgress.length,
              })}
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-6 px-4 pb-8">
            {groups.map((g) =>
              g.list.length === 0 ? null : (
                <section key={g.key} className="space-y-2">
                  <div className="flex items-baseline gap-2">
                    <h3 className="text-sm font-semibold text-foreground">{g.label}</h3>
                    <span className="text-xs text-muted-foreground">· {g.list.length}</span>
                    <span className="ms-auto text-[11px] text-muted-foreground">{g.hint}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {g.list.map((a) => (
                      <AchievementTile key={a.id} a={a} t={t} />
                    ))}
                  </div>
                </section>
              ),
            )}
          </div>
        </SheetContent>
      </Sheet>
    </Card>
  )
}
